import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '../components/ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  Heading1, Heading2, Heading3, Link as LinkIcon, Highlighter,
  Undo, Redo, Code, Quote, Minus,
} from 'lucide-react';

const MenuBar = ({ editor, placeholders }) => {
  if (!editor) return null;

  const btnClass = (active) =>
    `p-1.5 rounded transition-colors ${active ? 'bg-[#00A3A3]/15 text-[#00A3A3]' : 'text-[#5A6570] hover:bg-[#F0F2F4]'}`;

  const insertPlaceholder = (key) => {
    editor.chain().focus().insertContent(`{{${key}}}`).run();
  };

  return (
    <div className="border-b border-[#E2E6EA] p-2 flex flex-wrap gap-0.5 items-center bg-[#FAFBFC]">
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('underline'))} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('strike'))} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-[#E2E6EA] mx-1" />
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('heading', { level: 1 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('heading', { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('heading', { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <Heading3 className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-[#E2E6EA] mx-1" />
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive({ textAlign: 'left' }))} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive({ textAlign: 'center' }))} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive({ textAlign: 'right' }))} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        <AlignRight className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-[#E2E6EA] mx-1" />
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('blockquote'))} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass()} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass(editor.isActive('highlight'))} onClick={() => editor.chain().focus().toggleHighlight().run()}>
        <Highlighter className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-[#E2E6EA] mx-1" />
      <Button type="button" variant="ghost" size="icon" className={btnClass()} onClick={() => editor.chain().focus().undo().run()}>
        <Undo className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className={btnClass()} onClick={() => editor.chain().focus().redo().run()}>
        <Redo className="h-4 w-4" />
      </Button>

      {/* Placeholder insertion */}
      {placeholders && Object.keys(placeholders).length > 0 && (
        <>
          <div className="w-px h-6 bg-[#E2E6EA] mx-1" />
          <div className="relative group">
            <Button type="button" variant="outline" size="sm" className="text-xs h-7 border-[#00A3A3]/30 text-[#00A3A3]">
              <Code className="h-3 w-3 mr-1" />Insert Placeholder
            </Button>
            <div className="absolute top-full left-0 mt-1 bg-white border border-[#E2E6EA] rounded-lg shadow-lg z-50 hidden group-hover:block min-w-[200px] max-h-48 overflow-y-auto">
              {Object.entries(placeholders).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[#F0F2F4] text-[#3A3F45]"
                  onClick={() => insertPlaceholder(key)}
                >
                  <span className="font-mono text-[#00A3A3]">{`{{${key}}}`}</span>
                  <span className="ml-2 text-[#5A6570]">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const RichTextEditor = ({ content, onChange, placeholder, placeholders = {}, editable = true }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit includes these by default, but we use separate extensions for more control
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder || 'Start typing your template content...' }),
    ],
    content: content || '',
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Sync external content changes (e.g., file upload) into editor
  React.useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML();
      // Only update if content actually differs (avoid cursor jump)
      if (content && content !== currentHtml && content !== '<p></p>') {
        editor.commands.setContent(content, false);
      }
    }
  }, [content, editor]);

  return (
    <div className="border border-[#E2E6EA] rounded-lg overflow-hidden" data-testid="rich-text-editor">
      {editable && <MenuBar editor={editor} placeholders={placeholders} />}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[200px] focus-within:outline-none [&_.tiptap]:outline-none [&_.tiptap]:min-h-[180px] [&_.tiptap_p.is-editor-empty:first-child::before]:text-[#A8B1B9] [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none"
      />
    </div>
  );
};

export default RichTextEditor;
